/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'

import {
  KTCard,
  KTCardBody,
  Notes,
  DeleteModal,
  ArchiveModal,
  DataTables,
  Select,
  Timeline,
  ImageUpload,
  FastTaskUpdate,
  Loading,
  Datepicker,
} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {FormikProvider, FieldArray} from 'formik'
import {
  taskGetId,
  taskDelete,
  taskArchiving,
  taskCreate,
  taskProductCreate,
  taskProductDelete,
} from '../../../redux/slices/workplan.slice'
import {staffGetAll, allProductLookup, basicLookup, selectPlotsByBlock} from '../../../redux/slices/lookup.slice'
import {SourceType, TaskState, KTSVG, detailPageUrl, DetailPage} from '../../../helpers'
import Dropdown from 'react-bootstrap/Dropdown'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import {Modal} from 'react-bootstrap'
import {useMemoizedBreadcrumbs, useMemoizedPageUrl, useMemoizedValidationSchema, useMemoizedColumns} from '../../../utils/performanceOptimizations'

function Detail() {
  const [show, setShow] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const dispatch = useDispatch()
  const pageUrl = useMemoizedPageUrl(location)

  const selectWorkplanData = useMemo(
    () => (state) => ({
      detail: state.workplan.detail,
      isDeleted: state.workplan.isDeleted,
      isArchived: state.workplan.isArchived,
      error: state.workplan.error,
      loading: state.workplan.loading,
      isRedirect: state.workplan.isRedirect,
    }),
    []
  )

  const selectLookupData = useMemo(
    () => (state) => ({
      staffLookup: state.lookup.staffLookup,
      productLookup: state.lookup.productLookup,
      productUnitLookup: state.lookup.ProductUnit,
    }),
    []
  )

  const {detail, isDeleted, isArchived, error, loading, isRedirect} = useSelector(selectWorkplanData)
  const {staffLookup, productLookup, productUnitLookup} = useSelector(selectLookupData)
  const plots = useSelector(selectPlotsByBlock(detail?.projectId, detail?.constructionSiteId, detail?.blockId))

  useEffect(() => {
    dispatch(allProductLookup())
    dispatch(basicLookup('ProductUnit'))
  }, [dispatch])

  useEffect(() => {
    dispatch(taskGetId(id))
    dispatch(staffGetAll())
  }, [id, isRedirect, dispatch])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskStateId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const [formValues] = useState({
    Name: '',
    StartDate: new Date(),
    EndDate: new Date(),
    Duration: 0,
    TaskStateId: null,
    StaffId: null,
    ProjectId: '',
    Description: '',
    CompletionRate: 0,
    TotalPersonDay: 0,
    TotalWork: 0,
    PlotId: null,
    RootTaskId: id,
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      const result = await dispatch(taskCreate(values))
      if (taskCreate.rejected.match(result)) {
        setSubmitting(false)
      } else {
        setShowForm(false)
        resetForm()
      }
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    if (detail) {
      formik.setFieldValue('ProjectId', detail?.projectId)
      if (detail?.blockId) {
        if (detail?.plotId) formik.setFieldValue('PlotId', detail?.plotId)
        else formik.setFieldValue('PlotId', null)
      }
    }
  }, [detail])

  const handleProductSubmit = useCallback(
    async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      const validProducts = values.TaskProducts.filter((p) => p.ProductId)

      if (validProducts.length === 0) {
        setSubmitting(false)
        return
      }

      const hasInvalidAmount = validProducts.some((p) => !p.Amount || Number(p.Amount) <= 0)
      if (hasInvalidAmount) {
        setSubmitting(false)
        return
      }

      let hasError = false
      for (const product of validProducts) {
        const result = await dispatch(
          taskProductCreate({
            taskId: Number(id),
            productId: product.ProductId,
            productUnitId: product.ProductUnitId ?? null,
            amount: product.Amount,
            description: product.Description,
          })
        )
        if (taskProductCreate.rejected.match(result)) {
          hasError = true
          break
        }
      }
      if (!hasError) resetForm()
      setSubmitting(false)
    },
    [dispatch, id]
  )

  const productFormik = useFormik({
    initialValues: {TaskProducts: []},
    onSubmit: handleProductSubmit,
  })

  const onClickNewProduct = useCallback((arrayHelpers) => {
    arrayHelpers.push({ProductId: null, ProductUnitId: null, Amount: 0, Description: ''})
  }, [])

  const handleProductDelete = useCallback(
    async (taskProductId) => {
      await dispatch(taskProductDelete({id: taskProductId, taskId: Number(id)}))
    },
    [dispatch, id]
  )

  const handleDelete = useCallback(() => dispatch(taskDelete({id})), [dispatch, id])
  const handleArchive = useCallback(() => dispatch(taskArchiving({id})), [dispatch, id])

  const handleShowDelete = useCallback(() => setShow(true), [])
  const handleCloseDelete = useCallback(() => setShow(false), [])
  const handleShowArchive = useCallback(() => setShowArchive(true), [])
  const handleCloseArchive = useCallback(() => setShowArchive(false), [])
  const handleShowForm = useCallback(() => setShowForm(true), [])
  const handleCloseForm = useCallback(() => setShowForm(false), [])

  useEffect(() => {
    if (isDeleted || isArchived) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, isArchived, navigate, pageUrl])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.TASK'}),
        path: '/workplan/task',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  const subTaskColumns = useMemoizedColumns(
    [
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.NAME'}),
        prop: 'name',
        cell: (row) => (
          <Link
            to={detailPageUrl(DetailPage.Task, row?.id)}
            className='text-dark text-hover-primary'
            target='_blank'
          >
            {row.name}
          </Link>
        ),
        isSortable: true,
        isFilterable: true,
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.STAFFNAME'}),
        prop: 'staffName',
        isSortable: true,
        isFilterable: true,
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.STARTDATE'}),
        prop: 'startDate',
        cell: (row) =>
          row?.startDate
            ? intl.formatDate(new Date(row?.startDate), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : '-',
        isSortable: true,
        isFilterable: true,
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.ENDDATE'}),
        cell: (row) =>
          row?.endDate
            ? intl.formatDate(new Date(row?.endDate), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : '-',
        prop: 'endDate',
        isSortable: true,
        isFilterable: true,
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.TOTALWORK'}),
        prop: 'totalWork',
        cell: (row) => `${row.totalWork}`,
        isSortable: true,
        isFilterable: true,
        alignment: {
          horizontal: 'right',
        },
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.COMPLETIONRATE'}),
        prop: 'completionRate',
        cell: (row) => `%${row.completionRate}`,
        isSortable: true,
        isFilterable: true,
        alignment: {
          horizontal: 'right',
        },
      },
      {
        title: intl.formatMessage({id: 'TASK.SEARCH.TASKSTATENAME'}),
        prop: 'taskStateName',
        isSortable: true,
        isFilterable: true,
      },
    ],
    [intl]
  )

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.TASK.DETAIL'})}</PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail.name}</div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <KTSVG
                                path='/images/icons/duotune/general/gen044.svg'
                                className='svg-icon svg-icon-2 fs-4 me-1'
                              />
                              {detail.taskStateName}
                            </div>
                            {detail?.rootTaskName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <Link
                                    to={detailPageUrl(DetailPage.Task, detail?.rootTaskId)}
                                    className='text-gray-400 text-hover-primary'
                                    target='_blank'
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/arrows/arr066.svg'
                                      className='svg-icon svg-icon-2 fs-4 me-1'
                                    />
                                    {detail?.rootTaskName}
                                  </Link>
                                </div>
                              </>
                            )}

                            {detail?.projectName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/nav/Projects.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.projectName} - {detail?.constructionSiteName} -{' '}
                                  {detail?.blockName} - {detail?.plotName}
                                </div>
                              </>
                            )}

                            {detail?.staffName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/nav/Staff.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.staffName}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item
                                onClick={handleShowArchive}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.ARCHIVE'})}
                                </div>
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={handleShowDelete}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.CREATE.COMPANYNAME'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail?.companyId ? (
                                <Link
                                to={detailPageUrl(DetailPage.CompanySupplier, detail?.companyId)}
                                className='text-dark text-hover-primary'
                                target='_blank'
                                >
                                  {detail?.companyName}
                                </Link>
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </div>
                          </div>

                          {detail?.startDate && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({id: 'TASK.GET.STARTDATE'})}
                                </div>
                                <div className='fw-bold fs-4'>
                                  {intl.formatDate(new Date(detail?.startDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              </div>
                            </>
                          )}

                          {detail?.endDate && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({id: 'TASK.GET.ENDDATE'})}
                                </div>
                                <div className='fw-bold fs-4'>
                                  {intl.formatDate(new Date(detail?.endDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              </div>
                            </>
                          )}

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.GET.REMANINGDAY'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail?.remainingDay == 0
                                ? '-'
                                : detail?.remainingDay < 0
                                ? detail?.remainingDay * -1 +
                                  intl.formatMessage({id: 'PROJECT.DAYPASSED'})
                                : detail?.remainingDay +
                                  intl.formatMessage({id: 'PROJECT.DAYLEFT'})}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.GET.DURATION'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail?.duration} {intl.formatMessage({id: 'TASK.GET.DURATIONDAY'})}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.CREATE.TOTALPERSONDAY'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail?.totalPersonDay}{' '}
                              {intl.formatMessage({id: 'TASK.GET.DURATIONDAY'})}
                            </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.CREATE.TOTALWORK'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail?.totalWork} </div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'TASK.GET.COMPLETIONRATE'})}
                            </div>
                            <div className='fw-bold fs-4'>% {detail?.completionRate} </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {detail?.description && (
                    <>
                      <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
                        {detail?.description}
                      </div>
                    </>
                  )}
                </KTCardBody>
              </KTCard>

              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    <Tab eventKey='subTasks' title={intl.formatMessage({id: 'TASK.GET.SUBTASKS'})}>
                      <div className='fv-row fv-plugins-icon-container mt-5'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                          onClick={handleShowForm}
                        >
                          <KTSVG
                            path='/images/icons/duotune/arrows/arr009.svg'
                            className='svg-icon-2'
                          />
                          {intl.formatMessage({id: 'TASK.GET.SUBTASKS.ADD'})}
                        </button>
                      </div>

                      <div className='separator mt-4 mb-4'></div>

                      {detail?.subTasks?.length > 0 && (
                        <DataTables
                          columns={subTaskColumns}
                          data={detail?.subTasks}
                          exportTitle={intl.formatMessage({id: 'TASK.GET.SUBTASKS'})}
                        />
                      )}
                    </Tab>

                    <Tab
                      eventKey='taskProducts'
                      title={intl.formatMessage({id: 'TASK.GET.PRODUCTS'})}
                    >
                      <FormikProvider value={productFormik}>
                        <form onSubmit={productFormik.handleSubmit} noValidate>
                          <FieldArray
                            name='TaskProducts'
                            render={(arrayHelpers) => (
                              <>
                                <div className='fv-row fv-plugins-icon-container mt-5'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                    onClick={() => onClickNewProduct(arrayHelpers)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/arrows/arr009.svg'
                                      className='svg-icon-2'
                                    />
                                    {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                  </button>
                                </div>

                                {productFormik.values.TaskProducts.length > 0 && (
                                  <div className='table-responsive mt-4'>
                                    <table className='table align-middle gy-3 fs-6'>
                                      <thead>
                                        <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                          <th>{intl.formatMessage({id: 'TASK.PRODUCT.PRODUCTID'})}</th>
                                          <th>{intl.formatMessage({id: 'TASK.PRODUCT.PRODUCTUNITID'})}</th>
                                          <th>{intl.formatMessage({id: 'TASK.PRODUCT.AMOUNT'})}</th>
                                          <th>{intl.formatMessage({id: 'TASK.PRODUCT.DESCRIPTION'})}</th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {productFormik.values.TaskProducts.map((item, index) => (
                                          <tr key={index}>
                                            <td className='p-3' style={{minWidth: 220}}>
                                              <Select
                                                data={productLookup}
                                                value={item.ProductId}
                                                onChange={(opt) => {
                                                  productFormik.setFieldValue(
                                                    `TaskProducts[${index}].ProductId`,
                                                    opt?.value ?? null
                                                  )
                                                  productFormik.setFieldValue(
                                                    `TaskProducts[${index}].ProductUnitId`,
                                                    opt?.productUnitId ?? null
                                                  )
                                                }}
                                                menuPortalTarget={document.body}
                                              />
                                            </td>
                                            <td className='p-3' style={{minWidth: 160}}>
                                              <Select
                                                data={productUnitLookup}
                                                value={item.ProductUnitId}
                                                onChange={(opt) =>
                                                  productFormik.setFieldValue(
                                                    `TaskProducts[${index}].ProductUnitId`,
                                                    opt?.value ?? null
                                                  )
                                                }
                                                menuPortalTarget={document.body}
                                                isClearable
                                              />
                                            </td>
                                            <td className='p-3' style={{minWidth: 120}}>
                                              <input
                                                type='number'
                                                className='form-control form-control-solid'
                                                {...productFormik.getFieldProps(
                                                  `TaskProducts[${index}].Amount`
                                                )}
                                                onFocus={(e) => e.target.select()}
                                              />
                                            </td>
                                            <td className='p-3'>
                                              <input
                                                type='text'
                                                className='form-control form-control-solid'
                                                {...productFormik.getFieldProps(
                                                  `TaskProducts[${index}].Description`
                                                )}
                                              />
                                            </td>
                                            <td className='p-3'>
                                              <button
                                                type='button'
                                                className='btn btn-sm btn-icon btn-light-danger'
                                                onClick={() => arrayHelpers.remove(index)}
                                              >
                                                <KTSVG
                                                  path='/images/icons/duotune/general/gen027.svg'
                                                  className='svg-icon-2'
                                                />
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <div className='d-flex justify-content-end mt-3'>
                                      <button
                                        type='submit'
                                        className='btn btn-primary btn-sm'
                                        disabled={productFormik.isSubmitting}
                                      >
                                        {productFormik.isSubmitting ? (
                                          <span className='spinner-border spinner-border-sm me-2' />
                                        ) : null}
                                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </form>
                      </FormikProvider>

                      <div className='separator mt-5 mb-5'></div>

                      {detail?.taskProducts?.length > 0 && (
                        <div className='table-responsive'>
                          <table className='table align-middle gy-5 fs-6 table-striped table-hover'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>{intl.formatMessage({id: 'TASK.PRODUCT.PRODUCTID'})}</th>
                                <th>{intl.formatMessage({id: 'TASK.PRODUCT.AMOUNT'})}</th>
                                <th>{intl.formatMessage({id: 'TASK.PRODUCT.PRODUCTUNITID'})}</th>
                                <th>{intl.formatMessage({id: 'TASK.PRODUCT.DESCRIPTION'})}</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.taskProducts.map((item, index) => (
                                <tr key={index}>
                                  <td className='p-5'>{item?.productName}</td>
                                  <td className='p-5'>{item?.amount}</td>
                                  <td className='p-5'>{item?.productUnitName}</td>
                                  <td className='p-5'>{item?.description}</td>
                                  <td className='p-5'>
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-icon btn-light-danger'
                                      onClick={() => handleProductDelete(item.id)}
                                    >
                                      <KTSVG
                                        path='/images/icons/duotune/general/gen027.svg'
                                        className='svg-icon-2'
                                      />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                </KTCardBody>
              </KTCard>

              <ImageUpload SourceId={Number(id)} SourceTypeId={SourceType.Task} />
            </>
          )}
        </div>

        <div className='col-xl-3'>
          <FastTaskUpdate
            Id={Number(id)}
            TaskStateId={Number(detail?.taskStateId)}
            CompletionRate={Number(detail?.completionRate)}
          />
          <Notes SourceId={Number(id)} SourceTypeId={SourceType.Task} />
          <Timeline
            isRefresh={loading}
            SourceId={Number(id)}
            SourceTypeId={SourceType.Task}
            collapse={false}
          />
        </div>
      </div>

      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={handleCloseDelete}
        error={error}
      />

      <ArchiveModal
        onClick={handleArchive}
        onOpen={showArchive}
        onClose={handleCloseArchive}
        error={error}
      />

      <Modal
        className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
        id='kt_inbox_compose'
        role='dialog'
        data-backdrop='false'
        aria-hidden='true'
        tabIndex='-1'
        show={showForm}
        animation={false}
        aria-labelledby='contained-modal-title-vcenter'
        centered
        onHide={handleCloseForm}
        size='lg'
      >
        <div className='modal-content'>
          <form
              className='form position-relative'
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault()
              }}
              onSubmit={formik.handleSubmit}
              noValidate
            >
            <div className='d-flex align-items-center justify-content-between py-5 ps-8 pe-5 border-bottom'>
              <h5 className='fw-bold m-0'>{intl.formatMessage({id: 'TASK.GET.SUBTASKS.ADD'})}</h5>
              <div className='d-flex ms-2'>
                <div
                  className='btn btn-icon btn-sm btn-light-primary ms-2'
                  data-bs-dismiss='modal'
                  onClick={handleCloseForm}
                >
                  <KTSVG className='svg-icon-1' path='/images/icons/duotune/arrows/arr061.svg' />
                </div>
              </div>
            </div>

            <div className='d-block py-5 ps-8 pe-5 '>
              <div className='pt-5'>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 required fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.NAME'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <input
                          type='text'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('Name')}
                        />
                        {formik.touched.Name && formik.errors.Name && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.Name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 required fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.TASKSTATEID'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={TaskState}
                          required
                          {...formik.getFieldProps('TaskStateId')}
                          onChange={(item) => formik.setFieldValue('TaskStateId', item.value)}
                          textType={'intl'}
                          menuPortalTarget={null}
                        />
                        {formik.touched.TaskStateId && formik.errors.TaskStateId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.TaskStateId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.STARTDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container mb-6'>
                        <Datepicker
                          required
                          {...formik.getFieldProps('StartDate')}
                          onChange={([date]) => formik.setFieldValue('StartDate', date)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.ENDDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          required
                          {...formik.getFieldProps('EndDate')}
                          onChange={([date]) => formik.setFieldValue('EndDate', date)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.PLOTID'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container mb-6'>
                        <Select
                          data={plots}
                          {...formik.getFieldProps('PlotId')}
                          onChange={(item) => formik.setFieldValue('PlotId', Number(item?.value))}
                          menuPortalTarget={null}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.STAFFID'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container mb-6'>
                        <Select
                          data={staffLookup}
                          {...formik.getFieldProps('StaffId')}
                          onChange={(item) => formik.setFieldValue('StaffId', Number(item?.value))}
                          menuPortalTarget={null}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.TOTALWORK'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <input
                          type='number'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('TotalWork')}
                          onFocus={(event) => event.target.select()}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.TOTALPERSONDAY'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <input
                          type='number'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('TotalPersonDay')}
                          onFocus={(event) => event.target.select()}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.DURATION'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container mb-6'>
                        <input
                          type='number'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('Duration')}
                          onFocus={(event) => event.target.select()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <div className='row'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'TASK.CREATE.COMPLETIONRATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <input
                          type='number'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('CompletionRate')}
                          onFocus={(event) => event.target.select()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex align-items-center justify-content-center py-5 ps-8 pe-5 border-top'>
              <div className='d-flex align-items-center me-3'>
                <button type='button' className='btn btn-light me-3' onClick={handleCloseForm}>
                  {intl.formatMessage({id: 'BUTTON.CLOSE'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      {loading && <Loading />}
    </>
  )
}
export {Detail}
